import chatService from '@app/service/chatService';
import { getPeerChatCheck } from '@app/service/chatReadService';
import manageService from '@app/service/manageService';
import { isValidReservationId } from '@app/service/reservationIdGuard';
import { create } from 'zustand';
import { uniqueByKey } from '@utils/util';
import deviceStore from './deviceStore';
import notificationStore from './notificationStore';
import { isLooseEqual } from '@app/utils/equality';


const initData = {
  chattingRooms: [],
  incomeChat: null,
  dockedRoomId: null,
};
const size = 30;
const chatStore = create((set, get) => ({
  ...initData,
  resetRooms: async () => {
    const chattingRooms = get().chattingRooms;
    const newChattingRooms = [];

    const {
      appVersion,
      fcmPushToken,
      model,
      osType,
      osVersion,
      uuid,
    } = deviceStore.getState();
    const deviceInfoString = JSON.stringify({
      appVersion,
      fcmPushToken,
      model,
      osType,
      osVersion,
      uuid,
    });
    const messageString = JSON.stringify({
      callPage: `pc - resetRooms()`,
      chattingRooms: get().chattingRooms,
    });
    const logData = { deviceInfo: deviceInfoString, message: messageString };

    for (const chattingRoom of chattingRooms) {
      let newChattingRoom = { ...chattingRoom };
      const params = { size, page: 0, sort: 'seq,DESC' };
      const messagesProm = await chatService.getMessages(
        chattingRoom.id,
        params,
        logData,
      );
      if (
        chattingRoom == null ||
        chattingRoom?.id == null ||
        isLooseEqual(chattingRoom?.id, 'undefined') ||
        chattingRoom?.id == null ||
        isLooseEqual(chattingRoom?.id, 'null')
      )
        return;

      const messages = messagesProm.data.data.sort((a, b) => a.seq - b.seq);
      const totalPage = messagesProm.data.meta.totalPage;

      let checkSeq = null;
      if (messages && messages.length > 0) {
        const chatCheckProm = await getPeerChatCheck({
          bizId: chattingRoom.reservation?.travel?.id,
          businessType: 'TRAVEL',
          reservationId: chattingRoom.id,
        });
        const chatCheck = chatCheckProm.data.data;
        checkSeq = chatCheck?.checkLastSeq;
      }
      newChattingRoom.messages = messages;
      newChattingRoom.lastParams = params;
      newChattingRoom.totalPage = totalPage;
      newChattingRoom.checkSeq = checkSeq;
      newChattingRooms.push(newChattingRoom);
    }

    set({ chattingRooms: [...newChattingRooms], dockedRoomId: null });
  },
  // 문의 상세 옆에 표시할 채팅방은 예약 ID 하나로만 관리한다.
  setDockedRoomId: (id) => set({ dockedRoomId: id }),
  openChattingRoom: async (id) => {
    // 알림의 누락 ID는 예약·메시지 조회 전에 차단한다(기존 검사는 조회 뒤에 있었다).
    if (!isValidReservationId(id)) return;
    const chattingRooms = get().chattingRooms;
    const findIndex = chattingRooms.findIndex((room, i) => isLooseEqual(room.id, id));

    const {
      appVersion,
      fcmPushToken,
      model,
      osType,
      osVersion,
      uuid,
    } = deviceStore.getState();
    const deviceInfoString = JSON.stringify({
      appVersion,
      fcmPushToken,
      model,
      osType,
      osVersion,
      uuid,
    });
    const messageString = JSON.stringify({
      callPage: `pc - openChattingRoom(${id})`,
      chattingRooms: get().chattingRooms,
    });
    const logData = { deviceInfo: deviceInfoString, message: messageString };

    if (findIndex > -1) {
      chattingRooms[findIndex].open = true;
      chattingRooms[findIndex].newChat = false;
      set({ chattingRooms: [...chattingRooms] });
    } else {
      try {
        const reservationProm = await manageService.getReservationInfo(id);
        const reservation = reservationProm.data.data;

        const params = { size, page: 0, sort: 'seq,DESC' };
        const messagesProm = await chatService.getMessages(id, params, logData);
        if (id == null || isLooseEqual(id, 'undefined') || id == null || isLooseEqual(id, 'null'))
          return;
        const messages = messagesProm.data.data.sort((a, b) => a.seq - b.seq);
        const totalPage = messagesProm.data.meta.totalPage;

        let checkSeq = null;
        if (messages && messages.length > 0) {
          const chatCheckProm = await getPeerChatCheck({
            bizId: reservation?.travel?.id,
            businessType: 'TRAVEL',
            reservationId: id,
          });
          const chatCheck = chatCheckProm.data.data;
          checkSeq = chatCheck?.checkLastSeq;
        }

        chattingRooms.push({
          id,
          open: true,
          focus: true,
          reservation: reservation,
          lastParams: params,
          totalPage: totalPage,
          checkSeq: checkSeq,
          messages: messages,
          newChat: false,
        });
        set({ chattingRooms: [...chattingRooms] });
      } catch (e) {
        console.error('open ChattingRoom err : ', e);
      }
    }
  },
  closeChattingRoom: (id) => {
    const chattingRooms = get().chattingRooms;
    const findIndex = chattingRooms.findIndex((room, i) => isLooseEqual(room.id, id));
    if (findIndex > -1) {
      chattingRooms[findIndex].open = false;
    }
    set({ chattingRooms: [...chattingRooms] });
  },
  nextPage(id) {
    const chattingRooms = get().chattingRooms;
    const findIndex = chattingRooms.findIndex((room, i) => isLooseEqual(room.id, id));
    if (findIndex > -1) {
      let chattingRoom = chattingRooms[findIndex];
      if (isLooseEqual(chattingRoom.totalPage, 1)) {
        console.warn('마지막 페이지에 도달 했습니다.');
        return;
      }
      const lastParams = { ...chattingRoom.lastParams };
      lastParams.page = 0;
      lastParams.seq = chattingRoom.messages[0].seq;
      lastParams.seqType = 'BEFORE';
      chatService
        .getMessages(id, lastParams)
        .then((res) => {
          chattingRoom.messages = [
            ...res.data.data.sort((a, b) => a.seq - b.seq),
            ...chattingRoom.messages,
          ];
          chattingRoom.lastParams = lastParams;
          chattingRoom.totalPage = res.data.meta.totalPage;
          set({ chattingRooms: [...chattingRooms] });
        })
        .catch((e) => {});
    }
  },
  setIncomeChat: async (msg) => {
    console.log('setIncomeChat msg : ', msg);
    const newChattingRooms = [...get().chattingRooms];
    const findIndex = newChattingRooms.findIndex(
      (room, i) => isLooseEqual(room.id, msg.reservationId),
    );

    if (findIndex > -1) {
      newChattingRooms[findIndex].messages = uniqueByKey(
        [...newChattingRooms[findIndex].messages, msg],
        'seq',
      );
      if (!newChattingRooms[findIndex].open) {
        newChattingRooms[findIndex].newChat = true;
      }

      set({ chattingRooms: [...newChattingRooms] });

      if (
        newChattingRooms[findIndex].focus &&
        newChattingRooms[findIndex].open &&
        msg.fromBizType !== 'HOTEL'
      ) {
        try {
          const { checkNotificationGroup } = notificationStore?.getState();
          checkNotificationGroup(msg.seq, msg.reservationId);
          await chatService.messageCheck(msg.reservationId, msg.seq);
        } catch (e) {
          console.error('messsage check err : ', e);
        }
      }
    }

    set({ incomeChat: msg });
  },
  setFocus: async (id, isFocus) => {
    const newChattingRooms = [...get().chattingRooms];
    const findIndex = newChattingRooms.findIndex((room, i) => isLooseEqual(room.id, id));
    if (findIndex > -1) {
      newChattingRooms[findIndex].focus = isFocus;
      set({ chattingRooms: [...newChattingRooms] });
      // console.log('isFocus : ', isFocus);
      // console.log(newChattingRooms[0].focus);
      try {
        if (
          isFocus &&
          newChattingRooms[findIndex].open &&
          newChattingRooms[findIndex].messages.length > 0
        ) {
          const { checkNotificationGroup } = notificationStore?.getState();
          checkNotificationGroup(
            newChattingRooms[findIndex].messages[
              newChattingRooms[findIndex].messages?.length - 1
            ]?.seq,
            id,
          );
          await chatService.messageCheck(
            id,
            newChattingRooms[findIndex].messages[
              newChattingRooms[findIndex].messages?.length - 1
            ]?.seq,
          );
        }
      } catch (e) {
        console.error('messsage check err : ', e);
      }
    }
  },
  readMessage: (id, seq) => {
    const newChattingRooms = [...get().chattingRooms];
    const findIndex = newChattingRooms.findIndex((room, i) => isLooseEqual(room.id, id));
    if (findIndex > -1) {
      newChattingRooms[findIndex].checkSeq = seq;
      set({ chattingRooms: [...newChattingRooms] });
    }
  },
  removeChattingRoom: (id) => {
    const chattingRooms = get().chattingRooms;
    const findIndex = chattingRooms.findIndex((room, i) => isLooseEqual(room.id, id));
    if (findIndex > -1) {
      chattingRooms.splice(findIndex, 1);
      set({ chattingRooms: [...chattingRooms] });
    }
  },
  resetStore: () => {
    // console.log('initData??????????', initData);
    // TODO initData가 계속 바뀌는데 이유가 무엇인지??
    set({ chattingRooms: [], incomeChat: null, dockedRoomId: null });
  },
}));
export default chatStore;
